<template>
  <View direction="column" gap="lg">
    <Text variant="h1">Welcome back to Tallo</Text>
    <p class="user-email">{{ email }}</p>
    <div class="issue-description">
      <Icon :name="'info-circle'" />
      <Text variant="body-medium" class="what-is-going-on">{{ description }}</Text>
    </div>

    <slot></slot>
    <FormSeparator />

    <Button @click="sendOneTimeCode">Get one-time code</Button>
    <Link @click="requestPasswordResetLink" class="set-new-pass-link" :color="'accent'">Set new password</Link>
  </View>
</template>

<script setup lang="ts">
import { RouteLocationRaw, useRouter } from 'vue-router';

import { Button, FormSeparator, handleError, Icon, Link, Text, View } from '@tallo/design-system';
import { UserRole } from '@tallo/user';

import { useAuthStore } from '../auth.store';

const props = withDefaults(
  defineProps<{
    forRole: UserRole;
    email: string;
    confirmOneTimeCodeRoute: RouteLocationRaw;
    resetLinkSentRoute: RouteLocationRaw;
    /** Why there is no password to type; the default fits an account made with Google. */
    description?: string;
  }>(),
  {
    description:
      'This email was previously used to sign up with Google. Please log in with Google, get a one-time code, or set a new password to log in',
  },
);

const authStore = useAuthStore();
const router = useRouter();

const sendOneTimeCode = async () => {
  try {
    await authStore.requestOneTimeSignInCode(props.email);
    router.push(props.confirmOneTimeCodeRoute);
  } catch (error) {
    handleError(error);
  }
};

const requestPasswordResetLink = async () => {
  try {
    await authStore.sendPasswordResetLink(props.email, props.forRole);
    router.push(props.resetLinkSentRoute);
  } catch (error) {
    handleError(error);
  }
};
</script>

<style lang="scss" scoped>
.user-email {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.375rem 0rem;
  border-radius: var(--t-border-radius-sm);
  border: 0.063rem solid var(--t-color-neutral-300);
  font-weight: 600;
  line-height: 1.5rem;
  height: 2.5rem;
}

.issue-description {
  display: flex;
  padding: 1rem;
  gap: var(--t-spacing-sm);
  border-radius: var(--t-border-radius-sm);
  background-color: var(--t-color-neutral-100);
}

.what-is-going-on {
  line-height: 1.375rem;
  width: 100%;
}

.set-new-pass-link {
  align-self: center;
}
</style>
